import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Search, Plus, Dog, Phone, CalendarClock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { AdminLayout } from "./AdminLayout";
import { ClientForm } from "./ClientForm";
import { clientsApi, eur, fmtDate, formatApiErrorDetail, statsApi } from "./api";

export default function Dashboard() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [stats, setStats] = useState(null);

  const load = useCallback(async (q) => {
    setLoading(true);
    try {
      const res = await clientsApi.list(q);
      setClients(res.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => load(search), 300);
    return () => clearTimeout(t);
  }, [search, load]);

  useEffect(() => {
    statsApi.revenue().then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const createClient = async (data) => {
    try {
      const res = await clientsApi.create(data);
      toast.success("Ficha criada");
      setDialogOpen(false);
      navigate(`/admin/clients/${res.data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <AdminLayout>
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatCard label="Clientes" value={clients.length} testid="stat-clients" />
        <StatCard label="Visitas registadas" value={clients.reduce((s, c) => s + (c.visits_count || 0), 0)} testid="stat-visits" />
        <StatCard
          label="Faturação hoje"
          value={eur(stats?.day?.total || 0)}
          sub={`${stats?.day?.count || 0} marcações`}
          accent
          testid="stat-revenue-day"
        />
        <StatCard
          label="Faturação do mês"
          value={eur(stats?.month?.total || 0)}
          sub={`${stats?.month?.count || 0} marcações`}
          teal
          testid="stat-revenue-month"
        />
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display font-black text-4xl tracking-tight">Fichas de Clientes</h1>
          <p className="text-[#525252] text-sm mt-1">Histórico, fotos e visitas de cada patudo.</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-client-btn" className="bg-[#0A0A0A] hover:bg-[#E20074] text-white rounded-full self-start">
              <Plus size={18} className="mr-1" /> Nova ficha
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">Nova ficha de cliente</DialogTitle>
              <DialogDescription>Preencha os dados do dono e do cão para criar a ficha.</DialogDescription>
            </DialogHeader>
            <ClientForm onSubmit={createClient} submitLabel="Criar ficha" onCancel={() => setDialogOpen(false)} />
          </DialogContent>
        </Dialog>
      </div>

      <div className="relative mb-6 max-w-md">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#525252]" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Procurar por dono, cão, raça ou telefone…"
          className="pl-10 bg-white border-[#D4D4D4]"
          data-testid="search-input"
        />
      </div>

      {loading ? (
        <div className="py-20 flex justify-center">
          <div className="h-8 w-8 rounded-full border-2 border-[#E20074] border-t-transparent animate-spin" />
        </div>
      ) : clients.length === 0 ? (
        <div className="py-20 text-center" data-testid="empty-state">
          <Dog size={40} className="mx-auto text-[#D4D4D4] mb-3" />
          <p className="text-[#525252]">
            {search ? "Nenhuma ficha encontrada." : "Ainda não há fichas. Crie a primeira!"}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="clients-grid">
          {clients.map((c, i) => (
            <motion.button
              key={c.id}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: Math.min(i * 0.04, 0.3) }}
              onClick={() => navigate(`/admin/clients/${c.id}`)}
              data-testid={`client-card-${c.id}`}
              className="text-left bg-white border border-[#D4D4D4] rounded-xl overflow-hidden hover:border-[#0A0A0A] transition-colors group"
            >
              <div className="h-40 bg-[#0A0A0A] overflow-hidden flex items-center justify-center">
                {c.cover ? (
                  <img src={c.cover} alt={c.dog_name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                ) : (
                  <Dog size={40} className="text-[#333]" />
                )}
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="font-display font-bold text-lg leading-tight">{c.dog_name || "—"}</h3>
                    <p className="text-sm text-[#525252]">{c.dog_breed || "Sem raça indicada"}</p>
                  </div>
                  {c.photos_count > 0 && (
                    <span className="text-xs bg-[#F4F4F0] border border-[#D4D4D4] rounded-full px-2 py-0.5 text-[#525252]">
                      {c.photos_count} foto{c.photos_count > 1 ? "s" : ""}
                    </span>
                  )}
                </div>
                <div className="mt-3 pt-3 border-t border-[#eee] space-y-1.5 text-sm">
                  <p className="flex items-center gap-2 text-[#0A0A0A] font-medium">{c.owner_name}</p>
                  {c.phone && <p className="flex items-center gap-2 text-[#525252]"><Phone size={13} /> {c.phone}</p>}
                  <p className="flex items-center gap-2 text-[#525252]"><CalendarClock size={13} /> Última visita: {fmtDate(c.last_visit)}</p>
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <span className="text-xs text-[#525252]">{c.visits_count} visita{c.visits_count !== 1 ? "s" : ""}</span>
                  <span className="font-display font-bold text-[#E20074]">{eur(c.total_spent)}</span>
                </div>
              </div>
            </motion.button>
          ))}
        </div>
      )}
    </AdminLayout>
  );
}

function StatCard({ label, value, sub, accent, teal, testid }) {
  const bg = accent ? "bg-[#0A0A0A] border-[#0A0A0A] text-white"
    : teal ? "bg-[#00B4D8] border-[#00B4D8] text-[#003]"
    : "bg-white border-[#D4D4D4]";
  const labelCls = accent ? "text-[#a3a3a3]" : teal ? "text-[#003]/70" : "text-[#525252]";
  const valueCls = accent ? "text-[#00B4D8]" : "";
  return (
    <div data-testid={testid} className={`rounded-xl p-5 border ${bg}`}>
      <p className={`text-xs uppercase tracking-wide ${labelCls}`}>{label}</p>
      <p className={`font-display font-black text-3xl mt-1 ${valueCls}`}>{value}</p>
      {sub && <p className={`text-xs mt-1 ${labelCls}`}>{sub}</p>}
    </div>
  );
}
